/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Coffee, Play, Clock, ArrowsClockwise, CircleNotch, MoonStars,
} from "@phosphor-icons/react";

const HEARTBEAT_MS = 60_000;        // ping backend every 60s
const IDLE_MS = 3 * 60_000;         // 3 minutes -> auto-break
const TRACKED_ROLES = new Set(["office", "office_staff", "manager", "hr"]);

function fmtMs(ms) {
  if (!ms || ms < 0) return "0h 00m";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}
function fmtClockSecs(secs) {
  if (!secs || secs < 0) secs = 0;
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function PresenceWidget({ user }) {
  const enabled = user && TRACKED_ROLES.has(user.role);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoBreakReason, setAutoBreakReason] = useState(false);
  const [breakElapsedSec, setBreakElapsedSec] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const sessionRef = useRef(null);
  sessionRef.current = session;

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/my-duty/current");
      setSession(r.data);
      return r.data;
    } catch { return null; }
  }, []);

  // Auto-start a duty session on mount if none exists
  useEffect(() => {
    if (!enabled) return;
    (async () => {
      const cur = await refresh();
      if (!cur) {
        try {
          await api.post("/my-duty/start", {});
          await refresh();
        } catch { /* silently — admin opt-out roles already filtered */ }
      }
    })();
  }, [enabled]);

  // Track user activity for idle detection
  useEffect(() => {
    if (!enabled) return;
    const bump = () => { lastActivityRef.current = Date.now(); };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, bump, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, bump));
  }, [enabled]);

  // Heartbeat + idle / auto-resume loop
  useEffect(() => {
    if (!enabled) return;
    const tick = async () => {
      const cur = sessionRef.current;
      const idle = Date.now() - lastActivityRef.current;
      // Always send heartbeat (cheap, updates last_activity_ts on backend)
      try { await api.post("/my-duty/activity", {}); } catch { /* ignore */ }
      if (!cur) return;
      // Auto-break: ON_DUTY_ACTIVE + idle >= 3 min -> trigger break
      if (cur.status === "ON_DUTY_ACTIVE" && idle >= IDLE_MS) {
        try {
          await api.post("/my-duty/break?kind=auto", {});
          setAutoBreakReason(true);
          await refresh();
        } catch { /* ignore */ }
      }
      // Auto-resume: ON_BREAK with auto-reason + recent activity -> resume
      if (cur.status === "ON_BREAK" && autoBreakReason && idle < 5_000) {
        try {
          await api.post("/my-duty/resume", {});
          setAutoBreakReason(false);
          await refresh();
        } catch { /* ignore */ }
      }
    };
    tick();
    const id = setInterval(tick, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [enabled, autoBreakReason]);

  // Live tick for break timer + recalc current session totals
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      // bump break elapsed shown in UI
      const cur = sessionRef.current;
      if (cur && cur.status === "ON_BREAK") {
        const lastEvent = (cur.events || []).slice().reverse()
          .find(e => e.type === "break_start");
        if (lastEvent) {
          const startedAt = new Date(lastEvent.ts).getTime();
          setBreakElapsedSec(Math.max(0, (Date.now() - startedAt) / 1000));
        }
      } else {
        setBreakElapsedSec(0);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [enabled]);

  // Periodic refresh of totals
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [enabled]);

  const takeBreak = async () => {
    setBusy(true);
    try {
      await api.post("/my-duty/break", {});
      setAutoBreakReason(false);
      await refresh();
      toast.success("Break started");
    } catch (e) { toast.error("Could not start break"); }
    setBusy(false);
  };
  const stopBreak = async () => {
    setBusy(true);
    try {
      await api.post("/my-duty/resume", {});
      setAutoBreakReason(false);
      await refresh();
      toast.success("Welcome back");
    } catch (e) { toast.error("Could not resume"); }
    setBusy(false);
  };

  if (!enabled) return null;
  if (!session) {
    return (
      <PresenceShell title="Duty" status="loading">
        <div className="flex items-center gap-2 text-xs text-[#8A8780]">
          <CircleNotch size={12} className="animate-spin"/> starting session…
        </div>
      </PresenceShell>
    );
  }

  const onBreak = session.status === "ON_BREAK";
  return (
    <PresenceShell
      title="My Duty"
      status={onBreak ? "break" : "active"}
      autoBreak={onBreak && autoBreakReason}>
      <div className="space-y-3" data-testid="presence-widget">
        {/* Active timer */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <Stat label="Active" v={fmtMs(session.active_ms)} accent="#2D5A27"/>
          <Stat label="Break" v={fmtMs(session.break_ms)} accent="#C9A227"/>
          <Stat label="Idle" v={fmtMs(session.inactive_ms)} accent="#B12A1E"/>
        </div>
        {onBreak && (
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-900 flex items-center gap-2"
                data-testid="presence-break-banner">
            <Clock size={13} weight="duotone"/>
            {autoBreakReason
              ? <>Auto-paused after 3 min of no activity · <span className="font-mono">{fmtClockSecs(breakElapsedSec)}</span></>
              : <>On break · <span className="font-mono">{fmtClockSecs(breakElapsedSec)}</span></>}
          </div>
        )}
        <div className="flex gap-2">
          {!onBreak ? (
            <button onClick={takeBreak} disabled={busy}
                    data-testid="presence-take-break"
                    className="flex-1 inline-flex items-center justify-center gap-2 h-10 px-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm font-medium hover:bg-amber-100 disabled:opacity-60">
              <Coffee size={13} weight="duotone"/> Take a break
            </button>
          ) : (
            <button onClick={stopBreak} disabled={busy}
                    data-testid="presence-stop-break"
                    className="flex-1 inline-flex items-center justify-center gap-2 h-10 px-3 rounded-md bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800 disabled:opacity-60">
              <Play size={13} weight="fill"/> Resume work
            </button>
          )}
          <button onClick={refresh} title="Refresh"
                  data-testid="presence-refresh"
                  className="w-10 h-10 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 grid place-items-center">
            <ArrowsClockwise size={13}/>
          </button>
        </div>
        <div className="text-[10px] text-[#8A8780] text-center">
          Auto-break after 3 min idle · Heartbeat every 60 s
        </div>
      </div>
    </PresenceShell>
  );
}

function PresenceShell({ title, status, autoBreak, children }) {
  const tone = status === "break"
    ? { color: "#C9A227", bg: "from-amber-50/80 to-amber-50/30",
        border: "border-amber-200/60", label: autoBreak ? "Auto-paused" : "On break", Icon: MoonStars }
    : status === "loading"
      ? { color: "#8A8780", bg: "from-slate-50 to-white",
          border: "border-slate-200", label: "Initialising", Icon: CircleNotch }
      : { color: "#2D5A27", bg: "from-emerald-50/70 to-emerald-50/20",
          border: "border-emerald-200/50", label: "Active", Icon: Clock };
  return (
    <div className={`rounded-xl border ${tone.border} bg-gradient-to-br ${tone.bg} p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-md grid place-items-center"
              style={{ background: `${tone.color}1A`, color: tone.color }}>
          <tone.Icon size={14} weight="duotone"/>
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-700">{title}</div>
        <div className="ml-auto text-[10px] uppercase tracking-[0.18em] font-semibold"
              style={{ color: tone.color }}>{tone.label}</div>
      </div>
      {children}
    </div>
  );
}

function Stat({ label, v, accent }) {
  return (
    <div className="bg-white/80 rounded-md px-2 py-2 border border-slate-200/60">
      <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="font-heading text-sm mt-0.5" style={{ color: accent }}>{v}</div>
    </div>
  );
}
